/**
 * LiveBackground (web) — "live wallpaper" layer behind page content.
 *
 * Soft blurred gradient blobs in brand colours drift lava-lamp style and are
 * gently attracted to the cursor. Rendered as plain DOM nodes driven by a
 * single rAF loop that only writes `transform` (compositor-only, no React
 * re-renders), with `filter: blur()` rasterised once per blob.
 *
 * Sits absolutely behind the page scroll view (see ui/Screen.tsx); cards are
 * opaque so content always reads on top. `pointer-events: none` throughout —
 * the cursor is tracked on `window`, so blobs react even under cards.
 *
 * Honors reduce-motion (static blobs) and pauses in background tabs. Can be
 * turned off in Настройки → Оформление («Живой фон», pref.livebg).
 */
import { useEffect, useRef } from 'react'

import { useUiPrefs } from '../i18n'
import { ACCENTS, DEFAULT_ACCENT } from '../theme/accents'
import { prefersReducedMotion } from './motion'

interface BlobSpec {
  /** base position, fraction of container size */
  x: number
  y: number
  /** diameter, px */
  size: number
  /** drift amplitude (px) and speed (rad/s) per axis */
  ax: number
  ay: number
  sx: number
  sy: number
  phase: number
  color: string
}

const hexToRgba = (hex: string, a: number) => {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${a})`
}

/** Blob layout is fixed; colours resolve per accent/theme at render time. */
const LAYOUT = [
  { x: 0.12, y: 0.18, size: 460, ax: 70, ay: 46, sx: 0.21, sy: 0.16, phase: 0.0 },
  { x: 0.82, y: 0.12, size: 380, ax: 56, ay: 60, sx: 0.16, sy: 0.23, phase: 1.7 },
  { x: 0.5, y: 0.55, size: 520, ax: 90, ay: 54, sx: 0.12, sy: 0.19, phase: 3.1 },
  { x: 0.18, y: 0.85, size: 360, ax: 60, ay: 44, sx: 0.19, sy: 0.14, phase: 4.4 },
  { x: 0.88, y: 0.78, size: 420, ax: 74, ay: 58, sx: 0.14, sy: 0.21, phase: 5.6 },
] as const

/** How far the cursor is felt (px) and how strongly blobs lean toward it. */
const CURSOR_RANGE = 700
const CURSOR_PULL = 110

export function LiveBackground() {
  const { theme, accent } = useUiPrefs()
  const hostRef = useRef<HTMLDivElement>(null)

  const primary = (ACCENTS.find((a) => a.key === accent) ?? ACCENTS.find((a) => a.key === DEFAULT_ACCENT))!
  const alpha = theme === 'dark' ? 0.26 : 0.16
  const colors = [
    primary.shades.primary, // brand accent
    '#C2974E', // bronze (CHROME.bronzeSolid)
    '#2F8F6B', // tone green
    primary.shades.bright,
    '#B0863C', // tone gold
  ]

  const blobs: BlobSpec[] = LAYOUT.map((l, i) => ({ ...l, color: colors[i % colors.length] }))

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const els = Array.from(host.children) as HTMLDivElement[]
    let rect = host.getBoundingClientRect()
    const onResize = () => {
      rect = host.getBoundingClientRect()
    }
    window.addEventListener('resize', onResize)

    // Smoothed cursor influence per blob (px offsets), so reactions feel liquid.
    const infl = blobs.map(() => ({ x: 0, y: 0 }))
    const mouse = { x: -1e5, y: -1e5 }
    const onMove = (e: MouseEvent) => {
      mouse.x = e.clientX - rect.left
      mouse.y = e.clientY - rect.top
    }
    window.addEventListener('mousemove', onMove, { passive: true })

    const place = (t: number) => {
      for (let i = 0; i < blobs.length; i++) {
        const b = blobs[i]
        const cx = b.x * rect.width
        const cy = b.y * rect.height
        const dx = mouse.x - cx
        const dy = mouse.y - cy
        const dist = Math.hypot(dx, dy)
        const w = Math.max(0, 1 - dist / CURSOR_RANGE)
        // ease toward the cursor-shifted target — never snaps, always flows
        const k = 0.045
        infl[i].x += ((dist > 0 ? (dx / dist) * CURSOR_PULL * w : 0) - infl[i].x) * k
        infl[i].y += ((dist > 0 ? (dy / dist) * CURSOR_PULL * w : 0) - infl[i].y) * k
        const px = cx + Math.sin(t * b.sx + b.phase) * b.ax + infl[i].x - b.size / 2
        const py = cy + Math.cos(t * b.sy + b.phase) * b.ay + infl[i].y - b.size / 2
        els[i].style.transform = `translate3d(${px}px, ${py}px, 0)`
      }
    }

    if (prefersReducedMotion()) {
      place(0) // static composition, no loop
      return () => {
        window.removeEventListener('resize', onResize)
        window.removeEventListener('mousemove', onMove)
      }
    }

    let raf = 0
    const start = performance.now()
    const tick = (now: number) => {
      if (!document.hidden) place((now - start) / 1000)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('mousemove', onMove)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, accent])

  return (
    <div
      ref={hostRef}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {blobs.map((b, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: b.size,
            height: b.size,
            borderRadius: '50%',
            background: `radial-gradient(circle at 35% 35%, ${hexToRgba(b.color, alpha)}, ${hexToRgba(b.color, 0)} 70%)`,
            filter: 'blur(38px)',
            willChange: 'transform',
          }}
        />
      ))}
    </div>
  )
}
